import { useMemo, useState } from 'react';
import {
  Table,
  ActionIcon,
  Text,
  Menu,
  rem,
  TextInput,
  Paper,
  Stack,
  Badge,
  Group,
} from '@mantine/core';
import { IconDotsVertical, IconEdit, IconTrash, IconSearch } from '@tabler/icons-react';
import type { Person } from '@/types/models';

interface PeopleTableProps {
  people: Person[];
  onView: (person: Person) => void;
  onEdit: (person: Person) => void;
  onDelete: (person: Person) => void;
  isLoading?: boolean;
}

export function PeopleTable({ people, onView, onEdit, onDelete, isLoading }: PeopleTableProps) {
  const [search, setSearch] = useState('');

  const filteredPeople = useMemo(() => {
    return people.filter((person) => {
      const matchesSearch =
        search === '' ||
        person.full_name.toLowerCase().includes(search.toLowerCase()) ||
        person.email?.toLowerCase().includes(search.toLowerCase()) ||
        person.employee_id?.toLowerCase().includes(search.toLowerCase()) ||
        person.title?.toLowerCase().includes(search.toLowerCase());

      return matchesSearch;
    });
  }, [people, search]);

  const rows = filteredPeople.map((person) => (
    <Table.Tr
      key={person.id}
      style={{ cursor: 'pointer' }}
      onClick={() => onView(person)}
    >
      <Table.Td>
        <div>
          <Text size="sm" fw={500}>
            {person.full_name}
          </Text>
          {person.title && (
            <Text size="xs" c="dimmed">
              {person.title}
            </Text>
          )}
        </div>
      </Table.Td>
      <Table.Td>{person.email || '-'}</Table.Td>
      <Table.Td>{person.employee_id || '-'}</Table.Td>
      <Table.Td>{person.phone || '-'}</Table.Td>
      <Table.Td>
        {person.department ? (
          <Badge variant="light">{person.department.name}</Badge>
        ) : (
          '-'
        )}
      </Table.Td>
      <Table.Td>{person.location?.name || '-'}</Table.Td>
      <Table.Td>
        <Group gap="xs" justify="flex-end" onClick={(e) => e.stopPropagation()}>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => onEdit(person)}
            title="Edit"
          >
            <IconEdit size={16} />
          </ActionIcon>
          <Menu shadow="md" width={200} position="bottom-end">
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray">
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item
                color="red"
                leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}
                onClick={() => onDelete(person)}
              >
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Stack gap="md">
      <Paper p="md" withBorder>
        <TextInput
          placeholder="Search people..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ flex: 1 }}
        />
      </Paper>

      <Paper withBorder>
        <Table.ScrollContainer minWidth={800}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Employee ID</Table.Th>
                <Table.Th>Phone</Table.Th>
                <Table.Th>Department</Table.Th>
                <Table.Th>Location</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading ? (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text ta="center" py="xl" c="dimmed">
                      Loading people...
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : rows.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text ta="center" py="xl" c="dimmed">
                      No people found
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                rows
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>

      <Text size="sm" c="dimmed">
        Showing {filteredPeople.length} of {people.length} people
      </Text>
    </Stack>
  );
}
